import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Switch,
  FormControlLabel,
  Divider,
  Link as MuiLink,
} from '@mui/material';
import { fetchStop, updateStop, fetchPhotosForStop, setPhotoHidden } from '../../lib/stops';
import { supabase } from '../../supabaseClient';
import { Stop, Photo } from '../../types';

// Stop editor. Focus on the publishing-critical fields first:
//   - Title, context, notes
//   - Hide entire stop from public
//   - Per-photo hide/show toggle (grid)
// AI-text editing (edited_why_it_matters etc) comes in a followup commit.
export default function StopEditPage() {
  const { stopId } = useParams<{ stopId: string }>();
  const navigate = useNavigate();
  const [stop, setStop] = useState<Stop | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [context, setContext] = useState('');
  const [notes, setNotes] = useState('');
  const [hiddenFromShare, setHiddenFromShare] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!stopId) return;
    (async () => {
      const [s, p] = await Promise.all([fetchStop(stopId), fetchPhotosForStop(stopId)]);
      if (s.data) {
        setStop(s.data);
        setTitle(s.data.title);
        setContext(s.data.context ?? '');
        setNotes(s.data.notes ?? '');
        setHiddenFromShare(s.data.is_hidden_from_share);
      }
      setPhotos(p.data);

      // Resolve signed URLs for photos
      if (p.data.length > 0) {
        const paths = p.data.map((ph) => ph.storage_path);
        const { data: signed } = await supabase.storage.from('stop-photos').createSignedUrls(paths, 3600);
        const urlMap: Record<string, string> = {};
        (signed ?? []).forEach((s: any) => {
          const photo = p.data.find((ph) => ph.storage_path === s.path);
          if (photo && s.signedUrl) urlMap[photo.photo_id] = s.signedUrl;
        });
        setPhotoUrls(urlMap);
      }
      setLoading(false);
    })();
  }, [stopId]);

  async function handleSaveStop() {
    if (!stop) return;
    setSaveError(null);
    setSaveMessage(null);
    setSaving(true);
    const { data, error } = await updateStop(stop.stop_id, {
      title: title.trim(),
      context: context.trim() || null,
      notes: notes.trim() || null,
      is_hidden_from_share: hiddenFromShare,
    });
    setSaving(false);
    if (error) {
      setSaveError(error);
      return;
    }
    if (data) {
      setStop(data);
      setSaveMessage('Saved.');
    }
  }

  async function togglePhotoHidden(photo: Photo) {
    const next = !photo.is_hidden_from_share;
    // Optimistic update
    setPhotos((prev) => prev.map((p) => (p.photo_id === photo.photo_id ? { ...p, is_hidden_from_share: next } : p)));
    const { error } = await setPhotoHidden(photo.photo_id, next);
    if (error) {
      // Revert
      setPhotos((prev) => prev.map((p) => (p.photo_id === photo.photo_id ? { ...p, is_hidden_from_share: !next } : p)));
      setSaveError(error);
    }
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!stop) return <Typography color="text.secondary">Place not found.</Typography>;

  const visiblePhotoCount = photos.filter((p) => !p.is_hidden_from_share).length;

  return (
    <Box sx={{ maxWidth: 720 }}>
      <MuiLink component="button" onClick={() => navigate(`/stops/${stop.stop_id}`)} sx={{ display: 'block', mb: 2 }}>
        ← Back to place
      </MuiLink>
      <Typography variant="h4" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
        Edit place
      </Typography>

      <Stack spacing={3}>
        <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />

        <TextField
          label="About this place"
          value={context}
          onChange={(e) => setContext(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          helperText="Shown at the top of the public place page. This is the friend-voice paragraph."
        />

        <TextField
          label="Notes (private)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          helperText="Only visible to you. Not shown on the public page."
        />

        <FormControlLabel
          control={
            <Switch checked={hiddenFromShare} onChange={(e) => setHiddenFromShare(e.target.checked)} />
          }
          label={
            <Box>
              <Typography>Hide this place from public trip</Typography>
              <Typography variant="caption" color="text.secondary">
                The entire place (photos, AI text, everything) is skipped when the trip is published.
              </Typography>
            </Box>
          }
        />

        {saveMessage ? <Alert severity="success">{saveMessage}</Alert> : null}
        {saveError ? <Alert severity="error">{saveError}</Alert> : null}

        <Box>
          <Button variant="contained" onClick={handleSaveStop} disabled={saving || !title.trim()}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>Photos</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {visiblePhotoCount} of {photos.length} visible in public trip. Tap a photo to hide it from the public view.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1,
            }}
          >
            {photos.map((photo) => (
              <Box
                key={photo.photo_id}
                onClick={() => togglePhotoHidden(photo)}
                sx={{
                  aspectRatio: '1 / 1',
                  overflow: 'hidden',
                  borderRadius: 1,
                  bgcolor: 'divider',
                  cursor: 'pointer',
                  position: 'relative',
                  opacity: photo.is_hidden_from_share ? 0.35 : 1,
                  transition: 'opacity 150ms',
                  '&:hover': { outline: '2px solid', outlineColor: 'primary.main' },
                }}
              >
                {photoUrls[photo.photo_id] ? (
                  <img
                    src={photoUrls[photo.photo_id]}
                    alt=""
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      imageOrientation: 'from-image',
                    }}
                  />
                ) : null}
                {photo.is_hidden_from_share ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(0,0,0,0.7)',
                      color: '#fff',
                      fontSize: 10,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      letterSpacing: 0.4,
                    }}
                  >
                    HIDDEN
                  </Box>
                ) : null}
              </Box>
            ))}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
